import { Icon, Spinner, SpinnerSize, Stack, Text } from 'office-ui-fabric-react'
import React, { useEffect, useState } from 'react'
import { ScreenHeader } from '../../Components/ScreenHeader'
import {
  createExperiment,
  Experiment,
  ExperimentInput,
  ExperimentsAndTypesQueryResponse,
  ExperimentType,
  getExperimentsAndTypes,
  getExperimentsAndTypesQuery,
  isEnded,
  SelectableExperimentationGroup,
  setEndDate,
} from '../../Api/experiments'
import { DataValue, graphql } from 'react-apollo'
import { ExperimentsTable } from './ExperimentsTable'
import { CreateOrManageExperimentDialog } from './CreateOrManageExperimentDialog'
import { ConfirmationDialog } from '../../Components/ConfirmationDialog'
import './ExperimentsScreen.css'
import { Account } from '../../Api/integrations'

type DialogType = 'create' | 'duplicate' | 'manage' | 'stop'

interface Dialog {
  type: DialogType
  experiment?: Experiment
}

function ExperimentsScreen({
  data,
}: {
  data?: DataValue<ExperimentsAndTypesQueryResponse, {}>
}) {
  const isDataReady =
    data &&
    !data.loading &&
    !!data.experiments &&
    !!data.experimentTypes &&
    !!data.allAccounts &&
    !!data.experimentGroups
  const [dialog, setDialog] = useState<Dialog | null>(null)
  useEffect(() => {
    if (!isDataReady) {
      return
    }
    const pathMatch = window.location.hash.match(/\/#\/manage\/([^/]+)$/)
    if (!pathMatch) {
      return
    }
    const experimentId = pathMatch[1]
    const experiment = data!.experiments!.find(({ id }) => id === experimentId)
    setDialog({ type: 'manage', experiment })
  }, [setDialog, data, isDataReady])
  let {
    experiments,
    allAccounts,
    experimentTypes,
    experimentGroups: experimentationGroups,
  } = data!
  experiments = experiments!
  allAccounts = allAccounts!
  experimentTypes = experimentTypes!
  experimentationGroups = experimentationGroups!
  const [showStoppedExperiments, setShowStoppedExperiments] = useState<boolean>(
    localStorage.getItem('showStoppedExperiments') === 'true'
  )
  return (
    <>
      <Stack
        className="screen"
        horizontalAlign="start"
        verticalAlign="start"
        verticalFill
        style={{ width: '100%' }}
      >
        <ScreenHeader
          path={['Experiments']}
          buttons={[
            {
              name: 'Show stopped experiments',
              toggle: true,
              toggled: showStoppedExperiments,
              onClick: () => {
                setShowStoppedExperiments(!showStoppedExperiments)
                localStorage.setItem(
                  'showStoppedExperiments',
                  (!showStoppedExperiments).toString()
                )
              },
            },
            {
              name: 'Create Experiment',
              icon: 'Add',
              disabled: !isDataReady,
              onClick: () => {
                setDialog({ type: 'create' })
              },
            },
          ]}
        />
        <Stack
          className="experiments-screen"
          horizontalAlign="center"
          verticalAlign="start"
          horizontal
          wrap
          styles={{
            root: {
              width: '100%',
              overflow: 'auto',
            },
            inner: {
              padding: 60,
            },
          }}
        >
          {!isDataReady ? (
            <Stack
              horizontalAlign="center"
              verticalAlign="center"
              verticalFill
              style={{ width: '100%' }}
            >
              <Spinner
                size={SpinnerSize.large}
                labelPosition={'right'}
                label="Loading"
              />
            </Stack>
          ) : experiments.length ? (
            <ExperimentsTable
              experiments={experiments.filter(
                (experiment) => showStoppedExperiments || !isEnded(experiment)
              )}
              experimentTypes={experimentTypes}
              accounts={allAccounts}
              onDuplicateClick={(experiment: Experiment) =>
                setDialog({ type: 'duplicate', experiment })
              }
              onManageClick={(experiment: Experiment) => {
                setDialog({ type: 'manage', experiment })
                removeExperimentFromHash()
                addExperimentToHash(experiment.id)
              }}
              onStopClick={(experiment: Experiment) =>
                setDialog({ type: 'stop', experiment })
              }
            />
          ) : (
            <Empty />
          )}
        </Stack>
      </Stack>
      {dialog && dialog.type !== 'stop' ? (
        <CreateOrManageExperimentStatefulDialog
          experimentTypes={experimentTypes}
          accounts={allAccounts}
          experimentationGroups={experimentationGroups}
          experiment={
            dialog.type === 'duplicate' && dialog.experiment
              ? {
                  ...dialog.experiment,
                  startDate: new Date().toISOString(),
                  endDate: '',
                }
              : dialog.experiment
          }
          isNewExperiment={
            dialog.type === 'create' || dialog.type === 'duplicate'
          }
          onClose={() => {
            setDialog(null)
            removeExperimentFromHash()
          }}
        />
      ) : null}
      {dialog && dialog.type === 'stop' && !!dialog.experiment ? (
        <StopExperimentDialog
          experiment={dialog.experiment}
          onClose={() => {
            setDialog(null)
          }}
        />
      ) : null}
    </>
  )
}

function CreateOrManageExperimentStatefulDialog({
  experimentTypes,
  accounts,
  experimentationGroups,
  isNewExperiment,
  experiment,
  onClose,
}: {
  experimentTypes: ExperimentType[]
  accounts: Account[]
  experimentationGroups: SelectableExperimentationGroup[]
  isNewExperiment: boolean
  experiment?: Experiment
  onClose: () => void
}) {
  const [disabled, setDisabled] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  return (
    <CreateOrManageExperimentDialog
      experimentTypes={experimentTypes}
      accounts={accounts}
      experimentationGroups={experimentationGroups}
      isNewExperiment={isNewExperiment}
      experiment={experiment}
      disabled={disabled}
      onCreate={async (experiment: ExperimentInput) => {
        setDisabled(true)
        try {
          await createExperiment(experiment)
        } catch (error) {
          setError((error as any).message)
          setDisabled(false)
          return
        }
        onClose()
        getExperimentsAndTypes().catch(() => {})
      }}
      onSetEndDate={async ({ id, endDate }) => {
        setDisabled(true)
        try {
          await setEndDate({ id, endDate })
        } catch (error) {
          setError((error as any).message)
          setDisabled(false)
          return
        }
        onClose()
        getExperimentsAndTypes().catch(() => {})
      }}
      onCancel={onClose}
      error={error}
    />
  )
}

function StopExperimentDialog({
  experiment,
  onClose,
}: {
  experiment: Experiment
  onClose: () => void
}) {
  const [error, setError] = useState<string>('')
  const [submitting, setSubmitting] = useState<boolean>(false)
  return (
    <ConfirmationDialog
      dialogTitle="Do you want to stop this experiment?"
      confirmationText={`Are you sure you want to stop the experiment "${experiment.tag}" permanently? It will take effect in a few minutes.`}
      errorMessage={error}
      yesButtonText="Yes"
      noButtonText="No"
      isLoading={submitting}
      onCancel={onClose}
      onConfirm={async () => {
        setSubmitting(true)
        try {
          await setEndDate({
            id: experiment.id,
            endDate: new Date().toISOString(),
          })
        } catch (error) {
          setError((error as any).message)
          setSubmitting(false)
          return
        }
        onClose()
        getExperimentsAndTypes().catch(() => {})
      }}
    />
  )
}

function Empty() {
  return (
    <Stack
      horizontalAlign="center"
      verticalAlign="center"
      verticalFill
      style={{ width: '100%' }}
    >
      <Text
        variant="xLarge"
        style={{
          maxWidth: 600,
          padding: 20,
          textAlign: 'center',
        }}
      >
        There are no experiments configured yet. You can add an experiment by
        pressing the
        <i>Create Experiment</i>{' '}
        <Icon iconName="Add" style={{ verticalAlign: 'middle' }} />
        &nbsp;button.
      </Text>
    </Stack>
  )
}

const withGql = graphql<{}, ExperimentsAndTypesQueryResponse>(
  getExperimentsAndTypesQuery,
  {
    options: () => ({
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    }),
  }
)
const ExperimentsScreenWithGql = withGql(ExperimentsScreen)

export { ExperimentsScreenWithGql as ExperimentsScreen }

function removeExperimentFromHash() {
  window.location.hash = window.location.hash.replace(
    /\/#\/manage\/([^/]+)$/,
    ''
  )
}

function addExperimentToHash(experimentId: string) {
  window.location.hash = `${window.location.hash}/#/manage/${experimentId}`
}
